// src/pages/Resources/StatusPage.js
import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, Box, Divider, Grid, Chip, Alert } from '@mui/material';
import { CheckCircle, Warning, Error, Sync } from '@mui/icons-material';

const RENGIGS = {
  green: '#2E7D32',
  lightGreen: '#4CAF50',
  lime: '#CDDC39',
  dark: '#1A2A3A',
  light: '#F8FAFC',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
};

const StatusPage = () => {
  const [statuses, setStatuses] = useState([
    { name: 'API', status: 'operational', lastUpdate: new Date().toISOString() },
    { name: 'Web Application', status: 'operational', lastUpdate: new Date().toISOString() },
    { name: 'Database', status: 'operational', lastUpdate: new Date().toISOString() },
    { name: 'Payment Processing', status: 'operational', lastUpdate: new Date().toISOString() },
    { name: 'Email Service', status: 'operational', lastUpdate: new Date().toISOString() },
    { name: 'File Storage', status: 'operational', lastUpdate: new Date().toISOString() },
  ]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return <CheckCircle sx={{ color: '#4CAF50' }} />;
      case 'degraded': return <Warning sx={{ color: '#FF9800' }} />;
      case 'outage': return <Error sx={{ color: '#F44336' }} />;
      default: return <Sync sx={{ color: '#9E9E9E' }} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'operational': return 'Operational';
      case 'degraded': return 'Degraded Performance';
      case 'outage': return 'Service Outage';
      default: return 'Unknown';
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'operational': return 'success';
      case 'degraded': return 'warning';
      case 'outage': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, border: `1px solid ${RENGIGS.border}` }}>
        <Typography variant="h3" sx={{ fontWeight: 900, color: RENGIGS.dark, mb: 1 }}>
          System Status
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Real-time status of Rengigs services.
        </Typography>
        <Divider sx={{ mb: 4 }} />

        <Alert severity="success" sx={{ mb: 4 }}>
          All systems are operational. Last updated: {new Date().toLocaleString()}
        </Alert>

        <Grid container spacing={3}>
          {statuses.map((service) => (
            <Grid item xs={12} md={6} key={service.name}>
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getStatusIcon(service.status)}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{service.name}</Typography>
                </Box>
                <Chip
                  label={getStatusLabel(service.status)}
                  color={getStatusChipColor(service.status)}
                  size="small"
                />
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" sx={{ fontWeight: 800, color: RENGIGS.green, mb: 2 }}>Incident History</Typography>
        <Typography variant="body2" color="text.secondary">
          No recent incidents reported. All services have been stable for the past 30 days.
        </Typography>

        <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary' }}>
          Subscribe to status updates at <a href="/status/rss" style={{ color: RENGIGS.green }}>RSS feed</a>.
        </Typography>
      </Paper>
    </Container>
  );
};

export default StatusPage;